import styled from '@emotion/styled'
import { Spinner } from 'app/components/Common/Spinner'
import { FlexBox } from 'app/components/Layout/FlexBox'
import { HeaderContext } from 'app/containers/Header'
import { styles, theme } from 'app/theme'
import { useVocabularyData } from 'app/utils/vocabulary'
import { rgba } from 'emotion-rgba'
import React, { memo, useContext, useEffect, useState } from 'react'

export interface Props {
  modalStatus?: boolean
  syncroStatus?: SyncroStatus
}

export const Syncrobox = memo(function Syncrobox({
  modalStatus,
  syncroStatus,
}: Props) {
  const { languageCode, languagePrefix } = useContext(HeaderContext) as any

  const [firstLoad, setFirstLoad] = useState(true)

  useEffect(() => {
    const script = document.createElement('script')
    script.async = true

    if ((modalStatus && firstLoad) || syncroStatus) {
      setFirstLoad(false)

      script.innerHTML = `
    (function (i, s, o, g, r, a, m) {
        i['SBSyncroBoxParam'] = r; i[r] = i[r] || function () {
            (i[r].q = i[r].q || []).push(arguments)
        }, i[r].l = 1 * new Date(); a = s.createElement(o),
        m = s.getElementsByTagName(o)[0]; a.async = 1; a.src = g; m.parentNode.insertBefore(a, m)
    })(window, document, 'script', 'https://cdn.simplebooking.it/search-box-script.axd?IDA=10530','SBSyncroBox');
    SBSyncroBox({
        CodLang: '${
          languagePrefix ? languagePrefix.toLocaleUpperCase() : 'EN'
        }',
        
        Styles: {
          Theme: 'light-pink',
          FontFamily: "'${theme.fontFamily.heading}'",
          CustomColor: '${theme.colors.variants.neutralDark1}',
          CustomBGColor: 'transparent',
          CustomButtonBGColor: '${theme.colors.variants.primaryDark}',
          CustomButtonHoverBGColor: '${theme.colors.variants.neutralLight2}',
          CustomLinkColor: '${theme.colors.variants.neutralDark2}',
          CustomIconColor: '${theme.colors.variants.primaryDark}',
          CustomBoxShadowColorFocus: '${theme.colors.variants.primaryDark}',
          CustomBoxShadowColorHover: '${theme.colors.variants.neutralLight2}',
          CustomAccentColor: '${theme.colors.variants.primaryDark}',
          CustomAccentColorHover: '${theme.colors.variants.primaryDark}',
          CustomSelectedDaysColor: '${theme.colors.variants.neutralLight3}',
          CustomIntentSelectionColor: '${theme.colors.variants.neutralDark2}',
          CustomIntentSelectionDaysBGColor: '${theme.colors.variants.neutralDark2}',
          CustomColorHover: '${theme.colors.variants.neutralLight2}',
          CustomLabelColor: '${theme.colors.variants.primaryDark}',
          CustomLabelHoverColor: '${theme.colors.variants.neutralLight2}',
          CustomFieldBackgroundColor: 'transparent',
          CustomWidgetBGColor: '${theme.colors.variants.primaryDark}',
        },

        Labels: {
          CheckinDate: {'EN': 'Check in', 'IT': 'Check in', 'FR': 'Arrivée'},
          CheckoutDate: {'EN': 'Check out', 'IT': 'Check out', 'FR': 'Départ'},
          NumPersons: {'EN': 'Guests', 'IT': 'Ospiti', 'FR': 'Invités'},
          NumAdults: {'EN': 'Adults', 'IT': 'Adulti', 'FR': 'Adultes'},
          NumKids: {'EN': 'Kids', 'IT': 'Bambini', 'FR': 'Enfants'},
          CheckAvailability: {'EN': 'Book now', 'IT': 'Book now', 'FR': 'Book now'},
          PromoCode: {'EN': 'Promo Code', 'IT': 'Codice Promo', 'FR': 'Code promo'},
          ModCancReservation: {'EN': 'Modify/Cancel Reservation', 'IT': 'Modifica/Cancella Prenotazione', 'FR': 'Modifier/annuler la réservation'},
        },
    });
    `

      document.body.appendChild(script)
    }

    return () => {
      if (modalStatus && firstLoad) {
        document.body.removeChild(script)
      }
    }
  }, [modalStatus, firstLoad])

  return (
    <Container
      className={modalStatus ? 'open' : undefined}
      dial={4}
      onClick={(e: any) => e.stopPropagation()}
      row
      stretch
    >
      <LeftSide />

      <RightSide dial={5}>
        <Spinner
          variant="be"
          className={`spinner${modalStatus ? ' open' : ''}`}
        />
        <LabelTitle>{useVocabularyData('be-label', languageCode)}</LabelTitle>
        <Title>{useVocabularyData('be-title', languageCode)}</Title>

        <Widget
          id="sb-container"
          className={modalStatus ? 'open' : undefined}
        />
      </RightSide>
    </Container>
  )
})

const LeftSide = styled.div`
  width: 50%;
  transform: translateX(-100%);
  transition-delay: 0.6s;
`

const LabelTitle = styled.div`
  ${styles.label}

  color: ${({ theme }) => theme.colors.variants.neutralDark1};
  margin-bottom: 1rem;
  opacity: 0;
`

const Title = styled.div`
  ${styles.title.medium}

  text-align: center;
  margin-bottom: 0.75rem;
  max-width: 25rem;
  opacity: 0;

  @media (max-width: 1439px) {
    font-size: 2rem;
    line-height: 2rem;
    max-width: none;
  }
  @media (max-height: 830px) {
    margin-bottom: 0;
    max-width: none;
  }
`

const RightSide = styled(FlexBox)`
  width: 50%;
  background: ${({ theme }) => theme.colors.variants.neutralLight2};
  padding: 0 10vw;
  overflow-y: auto;
  transform: translateX(100%);
  transition-delay: 0.6s;

  @media (max-width: 1199px) {
    display: block;
    width: 100%;
    transform: none;
  }
  @media (max-height: 830px) {
    padding: 0 7vw;
  }

  #sb-container .sb__footer-link.sb-custom-color-hover:hover {
    color: ${({ theme }) => theme.colors.variants.primaryDark} !important;
  }

  #sb-container {
    width: 100%;
    .sb-custom-icon-color {
      fill: ${({ theme }) => theme.colors.variants.neutralLight2} !important;
      color: ${({ theme }) => theme.colors.variants.neutralLight2} !important;
    }

    .sb {
      padding: 0 !important;
    }
    .sb-custom-widget-element-hover-color:hover {
      color: ${({ theme }) => theme.colors.variants.primaryDark} !important;
    }
    .sb__footer-promo-input.sb-custom-bg-color {
      background: ${({ theme }) =>
        theme.colors.variants.neutralLight3} !important;
    }
    .sb__guests-children-age-select.sb-custom-bg-color {
      background: ${({ theme }) =>
        theme.colors.variants.neutralLight3} !important;
      border: none !important;
    }
    .sb-custom-box-shadow-color,
    .sb-custom-box-shadow-color-hover,
    .focus .sb-custom-box-shadow-color-focus {
      box-shadow: none !important;
      padding: 0;
      &:before {
        display: none !important;
      }
      &:hover {
        box-shadow: none !important;
      }
    }
    .sb-custom-box-shadow-color:focus {
      box-shadow: none !important;
    }
    .sb-custom-box-shadow-color {
      border-color: ${rgba(
        theme.colors.variants.neutralLight3,
        0.1,
      )} !important;
    }
    #sb-container_sb__form-field--checkavailability {
      height: auto;
      display: inline-block;
      width: auto;
      background: ${({ theme }) =>
        theme.colors.variants.primaryDark} !important;
      color: ${({ theme }) => theme.colors.variants.neutralLight3} !important;
      font-family: ${({ theme }) => theme.fontFamily.label};
      font-size: 0.875rem;
      font-weight: 500;
      letter-spacing: 0.05em;
      line-height: 1rem;
      border-radius: 2.375rem;
      margin-top: 3rem;
      padding: 0.6875rem 1.5rem;

      &:hover {
        background: ${({ theme }) =>
          theme.colors.variants.primaryDark} !important;
        color: ${({ theme }) => theme.colors.variants.neutralLight3} !important;
      }
    }
    .sb-screen-m .sb__dates {
      width: 100% !important;
    }
    .sb-screen-m .sb__guests-rooms {
      width: 100% !important;
      margin-top: 2.5rem;
      padding-left: 0;
      padding-right: 0;
    }
    .sb-screen-m .sb__btn--verify {
      width: 100% !important;
    }
    .sb-screen-m .sb__footer-actions {
      width: 100% !important;
      float: left !important;
    }
    .sb__btn--primary {
      min-width: 7.5rem;
      background: ${({ theme }) =>
        theme.colors.variants.neutralLight2} !important;
      border: 0.0625rem solid transparent;
      color: ${({ theme }) => theme.colors.variants.primaryDark} !important;
      font-size: 0.75rem;
      letter-spacing: 0.05em;
      margin-left: 0.5rem;
      text-align: center;
      &:hover {
        background: ${({ theme }) =>
          theme.colors.variants.neutralLight2} !important;
      }
    }
    .sb__btn--secondary {
      border: 0.0625rem solid
        ${({ theme }) => theme.colors.variants.neutralLight2};
      box-sizing: border-box;
      color: ${({ theme }) => theme.colors.variants.neutralLight2} !important;
      font-size: 0.75rem;
      letter-spacing: 0.05em;
      text-align: center;
      &:hover {
        background: ${({ theme }) =>
          theme.colors.variants.primaryDark} !important;
        color: ${({ theme }) => theme.colors.variants.neutralLight2} !important;
      }
    }
  }

  .sb__form {
    justify-content: center;
  }

  .sb__form-field-label {
    display: inline-block;
  }

  .sb__form-field-input {
    .sb__form-field-icon {
      width: 1.875rem;
      background: url('/svg/calendar.svg') no-repeat center right;
      background-size: 1rem;
      .icon {
        display: none;
      }
    }
  }

  .sb__form-field-date-wrapper {
    margin-top: 0.3125rem;
  }

  .sb__property {
    display: none;
  }

  .sb__calendar,
  .sb-screen-m .sb__calendar {
    width: auto;
  }
  .sb__guests,
  .sb-screen-m .sb__guests {
    left: 0;
  }

  .sb__calendar-day--valid.sb__calendar-day.intent-selection-start,
  .sb__calendar-day--valid.sb__calendar-day.intent-selection {
    box-shadow: ${({ theme }) => theme.colors.variants.neutralLight2} 0 0 0
      0.0625rem inset !important;
    color: ${({ theme }) => theme.colors.variants.neutralDark2} !important;
  }

  .sb__dates {
    .sb__form-field--checkin,
    .sb__form-field--checkout {
      width: 100%;
      border-bottom: 0.125rem solid
        ${rgba(theme.colors.variants.primaryDark, 0.2)};
      margin-top: 1.75rem;
      padding-bottom: 0.25rem;
      .sb__form-field-label {
        font-family: ${({ theme }) => theme.fontFamily.paragraph};
        font-size: 0.75rem;
        font-weight: 500;
        letter-spacing: 0.1em;
        line-height: 1rem;
        margin-bottom: 0.75rem;
        text-transform: uppercase;
      }
      .sb__form-field-date-wrapper {
        margin-top: 0;
        margin-right: 1.875rem;
        white-space: nowrap;
      }
      .sb__form-field-date-number {
        font-family: ${({ theme }) => theme.fontFamily.heading};
        font-size: 1.5rem;
        font-weight: 300;
        line-height: 2rem;
        margin-right: 0;
      }
      .sb__form-field-month-year {
        font-family: ${({ theme }) => theme.fontFamily.heading};
        font-size: 1.5rem;
        font-weight: 300;
        line-height: 2rem;
        margin-left: 0.5rem;
        text-transform: capitalize;
      }
      .sb__form-field-weekday {
        display: none;
      }
      .sb__form-field-input {
        display: flex;
        height: auto;
        border-radius: 0 !important;
        box-shadow: none !important;
      }
    }
    .sb__form-field--checkin {
      padding-left: 0;
    }
    .sb__calendar {
      max-width: 25rem;
      background: ${({ theme }) =>
        theme.colors.variants.neutralLight3} !important;
      border: 0;
      border-radius: 0;
      box-shadow: none !important;
      color: ${({ theme }) => theme.colors.variants.neutralDark2};
      margin-top: 0.75rem;
      margin-bottom: 0.75rem;
      padding: 1.25rem 0;
    }
    .sb__calendar-month-name {
      font-family: ${({ theme }) => theme.fontFamily.label};
      text-transform: capitalize;
    }
    .sb__calendar-day {
      border: 0 !important;
      font-family: ${({ theme }) => theme.fontFamily.paragraph};
      font-size: 0.8125rem;
    }
    .sb__calendar-btn--prev {
      left: 1rem;
    }
    .sb__calendar-btn--next {
      right: 1rem;
    }
    .sb__calendar-btn {
      width: 1.875rem;
      height: 1.875rem;
      box-shadow: none !important;
      color: ${({ theme }) => theme.colors.variants.neutralLight3} !important;
      margin-top: 0.75rem;
      .icon {
        width: 2.25rem;
        height: 2.25rem;
        fill: ${({ theme }) => theme.colors.variants.neutralDark2} !important;
        margin: -0.625rem 0 0 -1.125rem;
      }
    }
    .sb__calendar-weekdays {
      font-weight: 300;
      margin-bottom: 0.625rem;
      overflow: hidden;
    }
    .sb__calendar-days {
      .sb__calendar-day {
        border-radius: 0 !important;
      }
    }
  }

  .sb__guests {
    background: ${({ theme }) =>
      theme.colors.variants.neutralLight3} !important;
    border-radius: 0 !important;
    margin: 0.75rem 0;
    padding: 1.3125rem 1.5625rem 1.875rem !important;
    .sb__panel-actions {
      margin-top: 1.25rem;
      button {
        font-family: ${({ theme }) => theme.fontFamily.paragraph};
        font-size: 0.875rem;
        line-height: 1rem;
        margin-top: 0 !important;
        text-transform: uppercase;
      }
    }
  }

  .sb__guests-rooms {
    display: inline-flex;
    border-bottom: 0.125rem solid
      ${rgba(theme.colors.variants.primaryDark, 0.2)};
    margin-top: 1.75rem;
    padding-bottom: 0.25rem;
    .sb__form-field {
      .sb__form-field-label {
        font-family: ${({ theme }) => theme.fontFamily.paragraph};
        font-size: 0.75rem;
        font-weight: 500;
        letter-spacing: 0.1em;
        line-height: 1rem;
        margin-bottom: 0.75rem;
        text-transform: uppercase;
      }
      .sb__form-field-date-wrapper {
        margin-top: 0;
        margin-right: 1.875rem;
      }
      .sb__form-field-date-number {
        font-family: ${({ theme }) => theme.fontFamily.heading};
        font-size: 1.5rem;
        font-weight: 300;
        line-height: 2rem;
        margin-right: 0;
      }
      .sb__form-field-guests {
        font-family: ${({ theme }) => theme.fontFamily.heading};
        font-size: 1.5rem;
        font-weight: 300;
        line-height: 2rem;
        margin-left: 0.5rem;
        text-transform: capitalize;
      }
      .sb__form-field-rooms {
        display: none;
      }
      #sb-container_sb__form-field--guests {
        height: auto;
        box-shadow: none !important;
        border-radius: 0 !important;

        .sb__form-field-icon {
          background: url('/svg/arrow-down.svg') no-repeat center right;
        }
      }
    }
  }

  .sb__guests-room {
    padding-bottom: 0;
    .sb__guests-room-header {
      height: 1.75rem;
      margin-bottom: 1rem;
    }
    .sb__guests-room-label {
      color: ${({ theme }) => theme.colors.variants.neutralLight3} !important;
      font-family: ${({ theme }) => theme.fontFamily.paragraph};
      font-size: 1rem;
      line-height: 1.375rem;
      opacity: 1;
      text-transform: capitalize;
    }
    .sb__guests-room-header-divider {
      display: none;
    }
    .sb__guests-children-age-select {
      width: 3.75rem;
      height: 1.75rem;
      font-family: ${({ theme }) => theme.fontFamily.heading};
      font-size: 1.125rem;
      font-weight: 300;
      border-radius: 0 !important;
      padding: 0 0.75rem !important;
      text-align: center;
      transform: translateY(-0.125rem);
    }

    div.sb__guests-children-age
      span.sb__guests-children-age-label.sb-custom-label-color {
      filter: contrast(5);
    }
  }

  .sb__guests-add-room,
  .sb__guests-children-age-label {
    font-family: ${({ theme }) => theme.fontFamily.paragraph};
    font-size: 0.875rem;
    line-height: 1rem;
    margin-left: 0.75rem;
    text-transform: lowercase;

    .icon {
      margin-top: -0.1875rem;
    }
  }

  span.sb__footer-promo-label.sb-custom-label-color {
    color: ${({ theme }) => theme.colors.variants.neutralLight3} !important;
  }

  .sb__guests-room-remove {
    width: 1.75rem;
    height: 1.125rem;
    border-right: 0.0625rem solid
      ${({ theme }) => theme.colors.variants.neutralLight3};
    margin-left: 0.5rem;
    padding-left: 0.5rem;
    padding-right: 2.25rem;
    svg {
      opacity: 1;
      transform: scale(1.3) translateY(-0.125rem);
    }
  }

  .sb__guests-adults-label,
  .sb__guests-children-label {
    color: ${({ theme }) => theme.colors.variants.neutralLight2} !important;
    font-family: ${({ theme }) => theme.fontFamily.paragraph};
    font-size: 0.875rem;
    line-height: 1.125rem;
    padding: 0;
  }

  .sb__btn {
    border-radius: 0;
    &.sb__btn--block.sb__btn--verify {
      margin-top: 0;
      text-align: center;
    }
  }

  .sb__footer {
    text-align: center;
    .sb__footer-actions {
      display: inline-flex;
      width: auto;
      float: none;
      margin: 1.75rem auto 0;
      > a {
        display: none;
      }
      .sb__footer-link {
        margin-top: 0;
      }
      a {
        border-bottom: 0.0625rem solid
          ${({ theme }) => theme.colors.variants.neutralDark1};
        font-family: ${({ theme }) => theme.fontFamily.label};
        font-size: 0.75rem;
        font-weight: 300;
        letter-spacing: 0.05em;
        line-height: 1rem;
        text-transform: uppercase;
        &:after {
          display: none;
        }
        .icon {
          display: none !important;
        }
      }
      .sb__footer-link--promo {
        margin-right: 0;
      }
      .sb__footer-promo-wrapper {
        display: inline-block;
        white-space: nowrap;
        a {
          width: 100%;
          text-align: left;
        }
      }
      .sb__footer-promo {
        display: flex;
        border: 0 !important;
        border-radius: 0 !important;
        padding: 1rem !important;

        .sb__footer-promo-label.sb-custom-label-color {
          filter: contrast(5);
        }
        input {
          height: 2.125rem;
          font-family: ${({ theme }) => theme.fontFamily.paragraph};
          border-radius: 0 !important;
          padding: 0 0.75rem !important;
        }
      }
      .sb__footer-link--promo-value {
        display: none;
      }
      .sb__footer-promo-label {
        font-family: ${({ theme }) => theme.fontFamily.paragraph};
        font-size: 0.8125rem;
        color: ${({ theme }) => theme.colors.variants.neutralLight3} !important;
        line-height: 1rem;
      }
    }
    .sb__panel-actions {
      margin-top: 1.875rem;
      button {
        font-family: ${({ theme }) => theme.fontFamily.paragraph};
        font-size: 0.875rem;
        line-height: 1rem;
        margin-top: 0 !important;
        text-transform: uppercase;
      }
    }
  }
`

const Container = styled(FlexBox)`
  width: 100%;
  height: 100%;
  overflow-y: auto;
  position: fixed;
  top: 0;
  bottom: 0;
  left: 0;
  -webkit-overflow-scrolling: auto;
  &.open {
    ${LeftSide},
    ${RightSide} {
      transition: 0.6s ease-in-out;
      transform: none;
    }

    ${LabelTitle}, ${Title} {
      transition: 0.6s 0.6s ease-in-out;
      opacity: 1;
    }
  }
`

const Widget = styled.div`
  opacity: 0;
  transition: opacity 0.6s ease-in-out;

  &.open {
    opacity: 1;
    transition: opacity 0.6s 0.6s ease-in-out;
  }
`

export type SyncroStatus = 'default' | 'open'
